import React, { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Col,
  Typography,
  Checkbox,
  Input,
  Form,
  DatePicker,
  Button,
  Row,
} from 'antd';
import { recalculateCargos, sortCargos } from 'utils/cargos';
import {
  DECIMAL_PATTERN,
  decimalNormalizer,
  INTEGER_PATTERN,
  titleize,
  toInteger,
  toMoment,
} from 'utils/normalizers';
import { aplicacionesCargosEnum, tiposDatosEnum, tiposDatosFormulaEnum } from 'views/Tramites/GPM/enums';
import { TableAdeudoPadron } from 'views/Recaudacion/Caja/PanelPadron';
import Variables from 'views/Tramites/GPM/components/Variables';
import toCurrency from 'utils/toCurrency';
import Select from 'components/Select';
import { getTarifas } from 'api/configuracion/tarifas';
import { useSelector } from 'react-redux';
import round from 'utils/round';
import { createDecimalRegExp } from 'utils/patterns';

export const permissionConfig = {
  model: 'cargo',
  title: 'Cargos',
  permissions: [
    'add_cargo',
    'add_referenciadepago',
    'view_variable',
    'view_valorporvariabledeseleccion',
    'view_referenciadepago',
  ],
};

const { Text, Title } = Typography;
const mapId = (c) => c.id;
const ENV_NAME = process.env.REACT_APP_ENV_NAME;
const TLAJOMULCO = ENV_NAME === 'tlajomulco';

const integerRules = (required = false) => [
  { required },
  {
    validator: async (_, val) => {
      if (val && !INTEGER_PATTERN.test(val)) {
        throw Error('Ingrese un entero');
      }
    },
  },
];

const decimalRules = (required = false) => [
  { required },
  {
    validator: async (_, val) => {
      if (val && !DECIMAL_PATTERN.test(val)) {
        throw Error('Ingrese un decimal válido');
      }
    },
  },
];

export const getVariableRules = (tipoDeDato, required = true) => {
  if ([tiposDatosEnum.ENTERO, tiposDatosFormulaEnum.ENTERO]
    .includes(tipoDeDato)) {
    return integerRules(required);
  }
  if ([tiposDatosEnum.DECIMAL, tiposDatosFormulaEnum.DECIMAL]
    .includes(tipoDeDato)) {
    return decimalRules(required);
  }
  return [{ required }];
};

const isExpired = (record, date = toMoment(new Date())) => {
  if (!record.fecha_de_vencimiento) return true;
  return toMoment(record.fecha_de_vencimiento).isBefore(date);
};

export const getMandatoryKeys = (cargosTramite, mandatoryTypes = [], date) => {
  const matches = cargosTramite
    .filter((c) => !c.es_accesorio
      && mandatoryTypes.includes(c.tipo_de_cargo?.id) && isExpired(c.tipo_de_cargo, date))
    .map(mapId);
  const accesories = matches
    .map((m) => cargosTramite.filter((c) => c.es_accesorio && c.cargo_padre === m).map(mapId))
    .flat();
  return [...matches, ...accesories];
};

const renderCapturaDeImporte = (
  tc,
  selectedKeysTiposCargos,
  catalogoDeVariables,
  conceptosRef,
  valuesVariablesCargos,
  decimalRegExp,
) => {
  const disabled = !selectedKeysTiposCargos.includes(tc.id);
  if (tc.tipo_de_aplicacion === aplicacionesCargosEnum.CAPTURABLE) {
    return (
      <Form.Item
        label={`Importe ${(tc.importe_minimo || tc.importe_maximo) && `(${tc.importe_minimo} - ${tc.importe_maximo})`}`}
        name={[tc.id, 'importe']}
        rules={[
          {
            validator: async (_, value) => {
              if (value) {
                if (!decimalRegExp.test(value)) {
                  throw new Error('Ingrese un decimal válido');
                }
                if (value < tc.importe_minimo) {
                  throw new Error('Ingrese una cantidad mayor/igual al importe mínimo del cargo');
                }
                if (value > tc.importe_maximo) {
                  throw new Error('Ingrese una cantidad menor/igual al importe máximo del cargo');
                }
              } else {
                throw new Error('El campo es requerido');
              }
            },
          },
        ]}
        normalize={(val) => decimalNormalizer(val)}
      >
        <Input disabled={disabled} />
      </Form.Item>
    );
  }
  if ([aplicacionesCargosEnum.TARIFA, aplicacionesCargosEnum.MULTIPLO]
    .includes(tc.tipo_de_aplicacion)) {
    if (tc.tipo_de_dato) {
      return (
        <Form.Item
          name={[tc.id, tc.catalgo_de_tarifa ? 'variable' : 'importe']}
          label={`Introduzca ${tc.parametro_descripcion}`}
          rules={getVariableRules(tc.tipo_de_dato)}
          normalize={(val) => {
            if (tc.tipo_de_dato === tiposDatosEnum.ENTERO) {
              return toInteger(val);
            }
            if (tc.tipo_de_dato === tiposDatosEnum.DECIMAL) {
              return decimalNormalizer(val);
            }
            return val;
          }}
        >
          {tc.tipo_de_dato.id === tiposDatosEnum.RANGO_DE_FECHAS ? (
            <DatePicker.RangePicker
              disabled={disabled}
              disabledDate={(date) => date.isAfter(new Date())}
            />
          ) : (
            <Input disabled={disabled} allowClear />
          )}
        </Form.Item>
      );
    }
    return (
      <Form.Item
        name={[tc.id, 'importe']}
        rules={[{ required: true }]}
        label="Concepto"
      >
        <Select
          // disabled={loading}
          dataSource={conceptosRef.current[tc.id]?.results?.length}
          render={(e) => `${e.concepto} - ${toCurrency(e.impuesto_por_pagar || 0)}`}
          pagination={{
            getter: getTarifas,
            setter: (dataSource) => {
              // eslint-disable-next-line no-param-reassign
              conceptosRef.current[tc.id] = dataSource;
            },
            config: {
              qName: 'concepto',
              auto: true,
            },
            params: { catalogo_de_tarifa: tc.catalogo_de_tarifa },
          }}
        />
      </Form.Item>
    );
  }

  return (
    <Variables
      catalogoDeVariables={catalogoDeVariables}
      variables={tc.variables}
      prename={tc.id}
      valuesVariablesCargos={valuesVariablesCargos}
      showTotal={TLAJOMULCO && /Traslado de dominio \(parcial\)/i.test(tc.descripcion)}
    />
  );
};

const TableCargos = ({ cargos }) => (
  <Col span={24} style={{ padding: '15px 10px 30px 0' }}>
    <TableAdeudoPadron
      cargos={cargos}
      selectedRowKeys={cargos.map((c) => c.id)}
      tableProps={{
        restHeigth: 250,
        rowSelection: null,
        onRow: null,
      }}
    />
  </Col>
);

TableCargos.propTypes = { cargos: PropTypes.arrayOf(PropTypes.shape({})).isRequired };

const aplicacionesFijas = [aplicacionesCargosEnum.FIJO, aplicacionesCargosEnum.PORCENTAJE];

const Cargos = ({
  disabled: _disabled,
  // tramite,
  cargosTramite,
  periodosSeleccionados,
  setPeriodosSeleccionados,
  selectedKeysTiposCargos,
  setSelectedKeysTiposCargos,
  periodosFiscales,
  getAcuseTramite,
  getCotizacion,
  catalogoDeVariables,
  getRecibo,
  imprimirOrdenDePago,
  periodos,
  setCargosSaldados,
  cargosSaldados,
  tiposDeCargos,
  tiposDeCargosConsulta,
  valuesVariablesCargos,
  onCancelCargos,
  onRecalculateCargos,
  tipoCargoTotalizado,
}) => {
  const { ordenar_cargos_acendente, moneda } = useSelector((s) => s.auth.entidad);
  const [decimalRegExp] = createDecimalRegExp(moneda);
  const conceptosRef = useRef({});
  const disabled = cargosSaldados || _disabled;
  const tiposDeCargosNoGenerados = tiposDeCargos
    .filter((t) => !cargosTramite.some((c) => c.tipo_de_cargo.id === t.id))
    .sort((a, b) => (ordenar_cargos_acendente ? a.periodo_fiscal - b.periodo_fiscal
      : b.periodo_fiscal - a.periodo_fiscal));
  const mandatoyKeys = getMandatoryKeys(cargosTramite, tiposDeCargosConsulta);
  const _cargosConsulta = mandatoyKeys.map((k) => cargosTramite.find((c) => c.id === k));
  const _cargosGenerados = cargosTramite.filter((cargo) => {
    if (cargo.es_accesorio) {
      const cargoPadre = cargosTramite.find((c) => c.id === cargo.cargo_padre);
      return tiposDeCargos.some((tc) => tc.id === cargoPadre.tipo_de_cargo.id);
    }
    return tiposDeCargos.some((tc) => tc.id === cargo.tipo_de_cargo.id);
  });

  const cargosConsulta = useMemo(() => {
    const keys = cargosTramite.map((c) => c.id);
    return sortCargos(recalculateCargos(_cargosConsulta, keys));
  }, []);

  const cargosGenerados = useMemo(() => {
    const keys = cargosTramite.map((c) => c.id);
    return sortCargos(recalculateCargos(_cargosGenerados, keys));
  }, [cargosTramite]);

  const onChangeKeys = (checked, key) => {
    if (!cargosSaldados) {
      if (checked) {
        setSelectedKeysTiposCargos([...selectedKeysTiposCargos, key]);
      } else {
        setSelectedKeysTiposCargos(selectedKeysTiposCargos.filter((e) => e !== key));
      }
    }
  };

  const _cargos = [...cargosGenerados, ...cargosConsulta];
  const cargosConRecargosRecalculables = _cargos
    .filter((c) => c.recargos.some((r) => r.tipo_de_recargo.recalculable_manual));

  useEffect(() => {
    const _cargosSaldados = _cargos.length && _cargos.every((c) => c.saldado);
    if (tiposDeCargos.length && !_cargosGenerados.length) {
      setCargosSaldados(false);
    } else {
      setCargosSaldados(_cargosSaldados);
    }

    if (_cargosSaldados) {
      getRecibo(_cargos[0].id, false);
    } else {
      localStorage.setItem('totalizado', round(_cargos.reduce((acum, curr) => acum + curr.adeudo_total, 0)));
    }
  }, []);

  const hasAdeudos = !!(tiposDeCargos.length ? cargosGenerados : cargosConsulta).length;

  const renderCargo = (cargo, type = 'cargo') => (
    <Col span={24} key={cargo.id}>
      <Text type={type === 'cargo' ? 'success' : 'warning'}>
        {process.env.NODE_ENV === 'development' && `(${cargo.id}) - `}
        {type !== 'cargo' && !cargo.es_accesorio && `(${titleize(type)}) `}
        {cargo.es_accesorio && '(Accesorio) '}
        {[cargo.tipo_de_cargo?.periodo_fiscal?.periodo, cargo.tipo_de_cargo?.clave, cargo.descripcion].filter((e) => e).join(' - ')}
      </Text>
      {!cargo.saldado && (
        <Text type={type} style={{ float: 'right' }}>
          {toCurrency(cargo.subtotal_gpm || cargo.importe_total)}
        </Text>
      )}
      {cargosGenerados.filter((e) => e.cargo_padre === cargo.id && e.es_accesorio)
        .map((ac) => renderCargo(ac))}
      {cargo.recargos?.map((e) => renderCargo(e, 'recargo'))}
      {cargo.actualizaciones?.map((e) => renderCargo(e, 'actualización'))}
      {cargo.recargosMulta?.concat(cargo.gastosMulta)?.map((e) => renderCargo(e, 'multa'))}
    </Col>
  );

  const puedeGenerarCargos = !cargosSaldados && !!tiposDeCargosNoGenerados.length;

  return (
    <>
      {puedeGenerarCargos && (
        <>
          <Title level={3}>
            Selección de tipos de cargos a generar:
          </Title>
          <Col span={24} />
          <Checkbox.Group
            options={periodos}
            value={periodosSeleccionados}
            onChange={(checked) => setPeriodosSeleccionados(checked)}
            disabled={disabled}
          />
        </>
      )}
      {puedeGenerarCargos && tiposDeCargosNoGenerados
        .filter(((e) => periodosSeleccionados.includes(e.periodo_fiscal))).map((tc) => {
          const Wrapper = tc.variables.length ? React.Fragment : Col;
          const disabledTotalizado = tipoCargoTotalizado?.id === tc.id;
          return (
            <Col
              span={24}
              key={tc.id}
            // style={!disabled && selectedKeysTiposCargos.includes(tc.id)
            // && (!aplicacionesFijas.includes(tc.tipo_de_aplicacion)
            //   || tc.es_editable_fecha_de_vencimiento)
            //   ? { border: '1px dashed #000', marginBottom: 3 } : null}
            >
              <div>
                <Checkbox
                  onChange={({ target: { checked } }) => onChangeKeys(checked, tc.id)}
                  checked={selectedKeysTiposCargos.includes(tc.id)}
                  style={{ marginRight: 10 }}
                  disabled={disabled || disabledTotalizado}
                />
                <Text
                  delete={!selectedKeysTiposCargos.includes(tc.id)}
                  onClick={disabled || disabledTotalizado ? null
                    : () => onChangeKeys(!selectedKeysTiposCargos.includes(tc.id), tc.id)}
                  style={{ cursor: !disabled && 'pointer' }}
                >
                  {`${periodosFiscales.find((e) => e.id === tc.periodo_fiscal)?.periodo} - ${tc.clave} - ${tc.descripcion}`}
                </Text>
              </div>
              {!disabled && selectedKeysTiposCargos.includes(tc.id) && (
                <Row gutter={10} style={{ paddingLeft: 10 }}>
                  {!aplicacionesFijas.includes(tc.tipo_de_aplicacion) && (
                    <Wrapper>
                      {renderCapturaDeImporte(
                        tc,
                        selectedKeysTiposCargos,
                        catalogoDeVariables,
                        conceptosRef,
                        valuesVariablesCargos,
                        decimalRegExp,
                      )}
                    </Wrapper>
                  )}
                  {tc.es_editable_fecha_de_vencimiento && (
                    <Col xs={24} sm={24} md={8}>
                      <Form.Item
                        name={[tc.id, 'fecha_de_vencimiento']}
                        label="Fecha de vencimiento"
                        rules={[{ required: true }]}
                      >
                        <DatePicker disabled={valuesVariablesCargos[tc.id]?.fecha_de_vencimiento} format="DD/MM/YYYY" />
                      </Form.Item>
                    </Col>
                  )}
                </Row>
              )}
            </Col>
          );
        })}
      {puedeGenerarCargos && (<Col span={24} style={{ marginTop: 25 }} />)}
      {!!cargosGenerados.length && (
        <>
          <Row style={{ width: '100%' }}>
            <Text style={{ fontSize: 20 }}>
              Cargos
              {' '}
              {cargosSaldados ? 'saldados' : 'generados'}
            </Text>
          </Row>
          <Row style={{ width: '100%', paddingRight: 30 }}>
            {cargosGenerados.filter((e) => !e.es_accesorio).map((cargo) => renderCargo(cargo))}
          </Row>
        </>
      )}
      {(!!cargosGenerados.length || !!cargosConsulta.length)
        && (<Col span={24} style={{ marginTop: 20 }} />)}
      {!!tiposDeCargosConsulta.length && !!cargosConsulta.length && (
        <Col span={24}>
          <Row>
            <Text style={{ fontSize: 20 }}>
              Adeudo
            </Text>
          </Row>
          <TableCargos cargos={cargosConsulta} />
        </Col>
      )}
      {(!!cargosGenerados.length || !!cargosConsulta.length)
        && (<Col span={24} style={{ marginTop: 20 }} />)}
      {getAcuseTramite && (
        <Col span={8}>
          <Button block onClick={getAcuseTramite}>
            Obtener Acuse de Trámite
          </Button>
        </Col>
      )}
      <br />
      {cargosSaldados && (
        <Col span={8}>
          <Button block onClick={() => getRecibo(_cargos[0].id)}>
            Obtener
            {' '}
            {ENV_NAME !== 'juarez' ? 'Recibo' : 'Ticket'}
          </Button>
        </Col>
      )}
      {!cargosSaldados && hasAdeudos && (
        <>
          <Col span={8}>
            <Button
              block
              onClick={() => imprimirOrdenDePago(_cargos)}
              disabled={disabled || !!selectedKeysTiposCargos.length}
              id="btn-imprimir-orden-de-pago"
            >
              Obtener Orden de Pago
            </Button>
          </Col>
          {getCotizacion && (
            <Col span={8}>
              <Button block onClick={getCotizacion} disabled={disabled}>
                Obtener Cotización
              </Button>
            </Col>
          )}
          {!!onCancelCargos && (
            <Col span={8}>
              <Button
                block
                onClick={() => onCancelCargos(cargosGenerados, !!cargosConsulta.length)}
                disabled={disabled}
              >
                Cancelar Cargos
              </Button>
            </Col>
          )}
          {!!cargosConRecargosRecalculables.length && !!onRecalculateCargos && (
            <Col span={8}>
              <Button
                block
                onClick={() => onRecalculateCargos(cargosConRecargosRecalculables)}
                disabled={disabled}
              >
                Recalcular cargos
              </Button>
            </Col>
          )}
        </>
      )}
    </>
  );
};

Cargos.propTypes = {
  disabled: PropTypes.bool,
  tipoCargoTotalizado: PropTypes.shape({ id: PropTypes.number }),
  tramite: PropTypes.shape({
    object_id_padron: PropTypes.number,
    ciudadano: PropTypes.number,
    contribuyente: PropTypes.number,
  }).isRequired,
  cargosTramite: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.number.isRequired })).isRequired,
  tiposDeCargos: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  tiposDeCargosConsulta: PropTypes.arrayOf(PropTypes.number),
  periodosSeleccionados: PropTypes.arrayOf(PropTypes.number).isRequired,
  setPeriodosSeleccionados: PropTypes.func.isRequired,
  selectedKeysTiposCargos: PropTypes.arrayOf(PropTypes.number).isRequired,
  setSelectedKeysTiposCargos: PropTypes.func.isRequired,
  periodosFiscales: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  getAcuseTramite: PropTypes.func,
  getCotizacion: PropTypes.func,
  catalogoDeVariables: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  getRecibo: PropTypes.func.isRequired,
  imprimirOrdenDePago: PropTypes.func.isRequired,
  cargosSaldados: PropTypes.bool,
  setCargosSaldados: PropTypes.func.isRequired,
  onCancelCargos: PropTypes.func,
  periodos: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  valuesVariablesCargos: PropTypes.shape(),
  onRecalculateCargos: PropTypes.func,
};

Cargos.defaultProps = {
  disabled: false,
  cargosSaldados: false,
  tiposDeCargosConsulta: [],
  getAcuseTramite: null,
  getCotizacion: null,
  onCancelCargos: null,
  valuesVariablesCargos: {},
  onRecalculateCargos: null,
  tipoCargoTotalizado: null,
};

export default Cargos;
